import { useCallback, useEffect, useRef, useState } from 'react'

interface Props {
  content: string
  truncatedByDefault?: boolean
  maxLines?: number
}

/**
 * Hook for text truncation using CSS line-clamp with JS-based overflow detection.
 *
 * This approach is O(1) instead of O(n) - it uses CSS for visual truncation
 * and a single scrollHeight check to detect if truncation occurred.
 *
 * Usage:
 * 1. Attach `textRef` to your text container element
 * 2. Apply `truncatedClassName` to the container when `isTruncated` is true
 * 3. Use `shouldShowMoreButton` to conditionally render the More button
 * 4. Use `setIsTruncated` to toggle between truncated/expanded states
 */
export const useTruncateText = ({ content, truncatedByDefault = true, maxLines = 3 }: Props) => {
  const [isTruncated, setIsTruncated] = useState(truncatedByDefault)
  const [shouldShowMoreButton, setShouldShowMoreButton] = useState(false)
  const textRef = useRef<HTMLElement>(null)

  // Detect if content overflows when truncated
  const checkOverflow = useCallback(() => {
    const el = textRef.current
    if (!el) return

    // Temporarily apply truncation styles to measure
    const originalStyles = {
      display: el.style.display,
      webkitLineClamp: el.style.webkitLineClamp,
      webkitBoxOrient: el.style.webkitBoxOrient,
      overflow: el.style.overflow,
    }

    // Apply truncation styles
    el.style.display = '-webkit-box'
    el.style.webkitLineClamp = String(maxLines)
    el.style.webkitBoxOrient = 'vertical'
    el.style.overflow = 'hidden'

    // Check if content would be truncated
    const isOverflowing = el.scrollHeight > el.clientHeight

    // Restore original styles
    el.style.display = originalStyles.display
    el.style.webkitLineClamp = originalStyles.webkitLineClamp
    el.style.webkitBoxOrient = originalStyles.webkitBoxOrient
    el.style.overflow = originalStyles.overflow

    setShouldShowMoreButton(isOverflowing)
  }, [maxLines])

  // Check overflow when content changes or on mount
  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM has updated
    const rafId = requestAnimationFrame(() => {
      checkOverflow()
    })

    return () => cancelAnimationFrame(rafId)
  }, [content, checkOverflow])

  // Re-check on resize
  useEffect(() => {
    const handleResize = () => checkOverflow()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [checkOverflow])

  return {
    isTruncated,
    setIsTruncated,
    shouldShowMoreButton,
    textRef,
    maxLines,
  }
}

// CSS class to apply to truncated text containers
// Components should add this to their SCSS:
// .truncated {
//   display: -webkit-box;
//   -webkit-line-clamp: var(--max-lines, 3);
//   -webkit-box-orient: vertical;
//   overflow: hidden;
// }
